import moment from 'moment';

import { dateFormat } from '../../constants/apiDateFormat';
import { DATE_RANGE_PRESETS, DateRangePreset } from '../../constants/dateRangePreset';
import { DateRangePresetFilterRequest } from '../../interfaces/DateRangePresetFilterRequest';
import {
  getDayAfterToday,
  getDayBeforeDate,
  getYearFirstDay,
  getYearsBeforeDate,
} from '../dateUtils';

/**
 * Duration in days for each preset
 */
const PRESET_DURATION: Record<
  Exclude<DateRangePreset, typeof DATE_RANGE_PRESETS.YTD>,
  number
> = {
  [DATE_RANGE_PRESETS['7D']]: 7,
  [DATE_RANGE_PRESETS['4W']]: 28,
  [DATE_RANGE_PRESETS['3M']]: 90,
  [DATE_RANGE_PRESETS['1Y']]: 365,
};

/**
 * Calculates current and previous date ranges based on a preset.
 *
 * For 7D, 1M, 3M, 1Y: current period spans from today back by the preset duration,
 * previous period is the same-length window ending 1 day before the current period starts.
 *
 * For YTD: current period is Jan 1 of the current year to today,
 * previous period is the same calendar window in the prior year.
 */
export const getDateRangeByPreset = (
  preset: DateRangePreset,
): DateRangePresetFilterRequest => {
  const today = moment();

  // We use startOfNextDay (tomorrow 00:00) instead of today as the anchor point
  // because moment.subtract() does not include the current day in the range.
  // Subtracting N days from today yields a span of N days that ends yesterday.
  // By anchoring to tomorrow 00:00, the subtraction naturally shifts back one
  // extra day, making the current day the last included day in the range.
  // e.g. 7D on Apr 3: startOfNextDay (Apr 4) - 7 days = Mar 28
  //   → [Mar 28, Apr 4) effectively covers Mar 28 … Apr 3 = 7 days ✓
  const startOfNextDay = getDayAfterToday(1).startOf('day');

  if (preset === DATE_RANGE_PRESETS.YTD) {
    const currentStart = getYearFirstDay(today);
    const currentEnd = today.clone();
    const previousStart = getYearsBeforeDate(currentStart, 1);
    const previousEnd = getYearsBeforeDate(currentEnd, 1);

    return {
      currentPeriodStartDate: currentStart.format(dateFormat),
      currentPeriodEndDate: currentEnd.format(dateFormat),
      previousPeriodStartDate: previousStart.format(dateFormat),
      previousPeriodEndDate: previousEnd.format(dateFormat),
    };
  }

  const duration = PRESET_DURATION[preset];

  const currentEnd = today.clone();
  const currentStart = getDayBeforeDate(startOfNextDay, duration);
  const previousEnd = getDayBeforeDate(currentStart, 1);
  const previousStart = getDayBeforeDate(currentStart, duration);

  return {
    currentPeriodStartDate: currentStart.format(dateFormat),
    currentPeriodEndDate: currentEnd.format(dateFormat),
    previousPeriodStartDate: previousStart.format(dateFormat),
    previousPeriodEndDate: previousEnd.format(dateFormat),
  };
};
